import {useEffect, useMemo} from 'react'
import {Link, Navigate, Outlet, Route, Routes, useLocation} from 'react-router-dom'
import {Registration} from './components/Registration'
import {RegistrationReferral} from './components/RegistrationReferral'
import {ForgotPassword} from './components/ForgotPassword'
import {NewPassword} from './components/NewPassword'
import {Login} from './components/Login'
import {toAbsoluteUrl, useFeatureEnabled} from '../../../_gori/helpers'
import {useIntl} from 'react-intl'
import {ActiveAccount} from './components/ActiveAccount'
import {PendingActivation} from './components/PendingActivation'
import {ActiveUser} from './components/ActiveUser'
import {AcceptManager} from './components/AcceptManager'
import {FEATURES} from '../../../_gori/constants/_features'
import {ROUTES} from '../../../_gori/constants/_routes'
import LanguageToggle from '../common/components/LanguageToggle'
import {PARTNERS} from './core/_partners'

const AuthLayout = () => {
  const intl = useIntl()
  const location = useLocation()
  const isLoginPage = useMemo(
    () => location.pathname === ROUTES.AUTH.LOGIN || location.pathname === ROUTES.AUTH.BASE,
    [location.pathname]
  )
  const isRegistrationReferral = useMemo(
    () => location.pathname === ROUTES.AUTH.REGISTRATION_REFERRAL,
    [location.pathname]
  )
  const isRegistrationPage = useMemo(
    () =>
      location.pathname === ROUTES.AUTH.REGISTRATION ||
      location.pathname === ROUTES.AUTH.REGISTRATION_REFERRAL ||
      location.pathname === ROUTES.AUTH.ACTIVE_USER ||
      location.pathname === ROUTES.AUTH.ACCEPT_MANAGER,
    [location.pathname]
  )

  useEffect(() => {
    document.body.classList.add('bg-white')
    return () => {
      document.body.classList.remove('bg-white')
    }
  }, [])

  return (
    <div
      className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
      style={{
        backgroundColor: '#E4EFEE',
        backgroundImage: `url(${toAbsoluteUrl('/media/gori/login/gori_bg_rocket_1.webp')})`,
      }}
    >
      {/* begin::Content */}
      <div className='container d-flex flex-center flex-column flex-column-fluid'>
        {/* begin::Logo */}
        {isRegistrationReferral ? (
          <div
            className='d-flex align-items-center mb-12'
            style={{paddingTop: '80px', gap: '26px'}}
          >
            <img
              alt='Partner Logo'
              src={toAbsoluteUrl(
                Object.values(PARTNERS).find((p) => p.hash === location.hash.substring(1))?.logo ||
                  '/media/gori/referral-logos/portal_warehousing_logo.png'
              )}
              className='h-60px'
            />
            <img
              alt='X'
              src={toAbsoluteUrl('/media/gori/referral-logos/x.svg')}
              className='h-36px'
            />
            <img
              alt='ShipBae Logo'
              src={toAbsoluteUrl('/media/logos/big-shipbae-logo.png')}
              className='h-60px'
            />
          </div>
        ) : (
          <Link
            to={isLoginPage ? '/' : 'login'}
            className='mb-12'
            style={isRegistrationPage ? {paddingTop: '80px'} : undefined}
          >
            <img
              alt='ShipBae Logo'
              src={toAbsoluteUrl('/media/logos/big-shipbae-logo.png')}
              className='h-60px'
            />
          </Link>
        )}
        {/* end::Logo */}
        {/* begin::Wrapper */}
        <Outlet />

        {/* end::Wrapper */}
        {/* end::Content */}
        {/* begin::Footer */}
        <div className='d-flex flex-center flex-column-auto p-10 pb-12'>
          <div className='d-flex align-items-center fw-bold fs-6'>
            <a
              href='https://gori.ai'
              target='_blank'
              rel='noreferrer'
              className='text-muted text-hover-primary px-2'
            >
              {intl.formatMessage({id: 'ABOUT'})}
            </a>

            <Link to={ROUTES.SUPPORT.BASE} className='text-muted text-hover-primary px-2'>
              {intl.formatMessage({id: 'CONTACT_US'})}
            </Link>
          </div>
        </div>
        {/* end::Footer */}
      </div>
      <LanguageToggle />
    </div>
  )
}

const AuthPage = () => {
  const isRegisterEnabled = useFeatureEnabled(FEATURES.REGISTER)
  return (
    <>
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path={ROUTES.AUTH.ABSOLUTE.LOGIN} element={<Login />} />
          <Route path={ROUTES.AUTH.ABSOLUTE.FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={ROUTES.AUTH.ABSOLUTE.NEW_PASSWORD} element={<NewPassword />} />
          <Route path={ROUTES.AUTH.ABSOLUTE.ACCEPT_MANAGER} element={<AcceptManager />} />
          {isRegisterEnabled && (
            <>
              <Route path={ROUTES.AUTH.ABSOLUTE.REGISTRATION} element={<Registration />} />
              <Route
                path={ROUTES.AUTH.ABSOLUTE.REGISTRATION_REFERRAL}
                element={<RegistrationReferral />}
              />
              <Route path={ROUTES.AUTH.ABSOLUTE.ACTIVE_ACCOUNT} element={<ActiveAccount />} />
              <Route
                path={ROUTES.AUTH.ABSOLUTE.PENDING_ACTIVATION}
                element={<PendingActivation />}
              />
              <Route path={ROUTES.AUTH.ABSOLUTE.ACTIVE_USER} element={<ActiveUser />} />
            </>
          )}
          <Route path='*' element={<Navigate to={ROUTES.AUTH.ABSOLUTE.LOGIN} />} />
        </Route>
      </Routes>
    </>
  )
}

export {AuthPage}
